import React, { useMemo, useState } from "react";
import { FiSearch } from "react-icons/fi";

// The backend sends ISO timestamps with microseconds; trimmed to milliseconds
// so every browser parses them, then shown in the viewer's own time zone.
export function formatAuditTime(timestamp, language) {
    const date = new Date(String(timestamp || "").replace(/(\.\d{3})\d+/, "$1"));
    if (!timestamp || Number.isNaN(date.getTime())) return timestamp || "";
    return date.toLocaleString(language === "zh" ? "zh-TW" : "en-US", {
        dateStyle: "medium",
        timeStyle: "short",
    });
}

export default function AuditPanel({ audits, language, hasMore, onLoadMore }) {
    const zh = language === "zh";
    const [searchText, setSearchText] = useState("");
    const [loadingMore, setLoadingMore] = useState(false);

    // Searches the entries loaded so far; older ones come in with "Load older".
    const filteredAudits = useMemo(() => {
        const needle = searchText.trim().toLowerCase();
        if (!needle) return audits;
        return audits.filter((audit) =>
            [audit.actor, audit.action, audit.target_email, audit.target_uid, audit.details, audit.resource]
                .some((value) => String(value || "").toLowerCase().includes(needle)),
        );
    }, [audits, searchText]);

    const loadMore = async () => {
        setLoadingMore(true);
        try {
            await onLoadMore();
        } finally {
            setLoadingMore(false);
        }
    };

    return (
        <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-gray-900">
                {zh ? "審計日誌" : "Audit Logs"}
            </h2>
            <div className="bg-white rounded-lg shadow">
                <div className="px-5 py-3 border-b border-gray-200 bg-gray-50">
                    <div className="relative">
                        <FiSearch
                            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                            aria-hidden="true"
                        />
                        <input
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            placeholder={
                                zh
                                    ? "搜尋已載入的紀錄：電子郵件、動作或內容"
                                    : "Search loaded entries: email, action or details"
                            }
                            className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm bg-white focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
                        />
                    </div>
                </div>
                <div className="divide-y divide-gray-200 max-h-[32rem] overflow-y-auto">
                    {filteredAudits.map((audit) => (
                        <div key={audit.id} className="p-6 hover:bg-gray-50">
                            <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0">
                                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                                        <svg
                                            className="w-5 h-5 text-gray-600"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                            />
                                        </svg>
                                    </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex md:flex-row flex-col gap-2 md:items-center items-start justify-between mb-2">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-sm font-medium text-gray-900">
                                                {audit.user || audit.actor}
                                            </span>
                                            {audit.action && (
                                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                                    {audit.action}
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-sm text-gray-500">
                                            {formatAuditTime(audit.timestamp, language)}
                                        </span>
                                    </div>
                                    {(audit.target_email || audit.target_uid) && (
                                        <p className="text-sm text-gray-900 mb-1">
                                            <span className="text-gray-500">
                                                {zh ? "對象：" : "Target: "}
                                            </span>
                                            {audit.target_email || audit.target_uid}
                                        </p>
                                    )}
                                    <p className="text-sm text-gray-700 mb-2 break-words">
                                        {audit.description || audit.details}
                                    </p>
                                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                                        <span>
                                            IP:{" "}
                                            {audit.ipAddress ||
                                                audit.ip_address}
                                        </span>
                                        <span>
                                            Resource:{" "}
                                            {audit.resource || "System"}
                                        </span>
                                        {audit.details && audit.description && (
                                            <span>
                                                Details: {audit.details}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    {filteredAudits.length === 0 && (
                        <div className="p-6 text-center text-gray-500">
                            {searchText.trim()
                                ? zh
                                    ? "已載入的紀錄中沒有符合的項目"
                                    : "No matching entries among those loaded"
                                : zh
                                    ? "尚無審計日誌"
                                    : "No audit logs found"}
                        </div>
                    )}
                </div>
                {hasMore && (
                    <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between gap-3 text-sm text-gray-500">
                        <span>
                            {zh
                                ? `已載入 ${audits.length} 筆`
                                : `${audits.length} entries loaded`}
                        </span>
                        <button
                            type="button"
                            onClick={loadMore}
                            disabled={loadingMore}
                            className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {loadingMore
                                ? zh
                                    ? "載入中…"
                                    : "Loading…"
                                : zh
                                    ? "載入較舊的紀錄"
                                    : "Load older entries"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
